import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  CalendarDays,
  Files,
  LayoutDashboard,
  ListTodo,
  Lock,
  MoreHorizontal,
  Pencil,
  Printer,
  Trash2,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useAuth } from '@/app/AuthProvider';
import {
  useDeleteEngProject,
  useEngProject,
  useEngSignedUrl,
  useUpdateEngProject,
  type EngProject,
} from '@/features/engineering/hooks';
import {
  canEditEngProject,
  canManageEngProject,
  getEngStatusMeta,
  isEngLockedStatus,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { formatDateOnly, formatDateTime, todayIsoDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { EngStatusBadge, PersonChip } from '@/components/engineering/EngParts';
import { EngStatusControl } from '@/components/engineering/EngStatusControl';
import { EditEngProjectDialog } from '@/components/engineering/EditEngProjectDialog';
import { EngOverviewTab } from '@/components/engineering/EngOverviewTab';
import { EngFilesTab } from '@/components/engineering/EngFilesTab';
import { EngPrintsTab } from '@/components/engineering/EngPrintsTab';
import { EngTasksTab } from '@/components/engineering/EngTasksTab';
import { EngBudgetTab } from '@/components/engineering/EngBudgetTab';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'files', label: 'Files', icon: Files },
  { id: 'prints', label: '3D prints', icon: Printer },
  { id: 'tasks', label: 'Tasks', icon: ListTodo },
  { id: 'budget', label: 'Budget', icon: Wallet },
] as const;
type TabId = (typeof TABS)[number]['id'];
const TAB_IDS: readonly string[] = TABS.map((t) => t.id);

/**
 * One engineering project: header with status control and cover,
 * then Overview / Files / 3D prints / Tasks / Budget tabs mirrored to
 * `?tab=` so links can land on a panel.
 */
export function EngineeringProjectPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { role, profile } = useAuth();
  const { data: project, isLoading, error } = useEngProject(id);
  const update = useUpdateEngProject();
  const remove = useDeleteEngProject();
  const [editOpen, setEditOpen] = useState(false);
  const { data: coverUrl } = useEngSignedUrl(project?.image_path ?? null);

  const memberIds = useMemo(() => (project?.eng_project_members ?? []).map((m) => m.profile_id), [project]);
  const ctx = { role, profileId: profile?.id, project, memberIds };
  const canManage = canManageEngProject(ctx);
  const canEdit = canEditEngProject(ctx);

  const tabParam = params.get('tab');
  const active: TabId = tabParam && TAB_IDS.includes(tabParam) ? (tabParam as TabId) : 'overview';
  const goToTab = (tab: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next, { replace: true });
  };

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink />
        <EmptyState title="Could not load this project" detail={formatError(error)} />
      </div>
    );
  }

  if (isLoading || !project) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader eyebrow="Engineering" title="Loading project…" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const locked = isEngLockedStatus(project.status);
  const archived = !!project.archived_at;
  const today = todayIsoDate();
  const late = !!project.target_date && project.target_date < today && !locked && project.status !== 'cancelled';

  const toggleArchive = async () => {
    try {
      await update.mutateAsync({ id: project.id, patch: { archived_at: archived ? null : new Date().toISOString() } });
      toast.success(archived ? 'Project restored.' : 'Project archived.');
    } catch (err) {
      toast.error(formatError(err, 'Could not update the project.'));
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm(
      `Delete “${project.code} · ${project.name}” and every file, version, print and cost in it? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await remove.mutateAsync({ id: project.id });
      toast.success('Project deleted.');
      navigate('/engineering', { replace: true });
    } catch (err) {
      toast.error(formatError(err, 'Could not delete the project.'));
    }
  };

  return (
    <div className="space-y-6">
      <BackLink />

      <div className={cn('flex flex-col gap-4 lg:flex-row lg:items-start', coverUrl && 'lg:gap-6')}>
        {coverUrl && (
          <img
            src={coverUrl}
            alt=""
            className="h-28 w-full shrink-0 rounded-lg border border-border object-cover lg:h-24 lg:w-40"
          />
        )}
        <PageHeader
          className="min-w-0 flex-1"
          eyebrow={
            <span className="inline-flex items-center gap-2">
              <Link to="/engineering" className="hover:underline">
                Engineering
              </Link>
              <span aria-hidden>/</span>
              <span className="font-mono">{project.code}</span>
            </span>
          }
          title={project.name}
          meta={
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <EngStatusBadge status={project.status} />
              {archived && <span className="text-xs uppercase tracking-wide text-muted-foreground">Archived</span>}
              <PersonChip person={project.owner} size="xs" />
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" />
                {project.start_date ? formatDateOnly(project.start_date) : 'No start'}
                {' → '}
                <span className={cn(late && 'font-medium text-fresh-salmon-dark')}>
                  {project.target_date ? formatDateOnly(project.target_date) : 'no target'}
                </span>
              </span>
            </span>
          }
          actions={
            <div className="flex items-center gap-2">
              <EngStatusControl project={project} disabled={!canEdit || archived} />
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
              )}
              {canManage && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Project actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={toggleArchive}>
                      {archived ? <ArchiveRestore className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
                      {archived ? 'Restore project' : 'Archive project'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={handleDelete}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete project…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          }
        />
      </div>

      {locked && <LockBanner project={project} />}

      <Tabs value={active} onValueChange={goToTab}>
        <TabsList className="h-auto flex-wrap gap-1 bg-secondary p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:shadow-sm">
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-6">
          <EngOverviewTab project={project} canEdit={canEdit && !archived} canManage={canManage && !archived} onGoToTab={goToTab} />
        </TabsContent>
        <TabsContent value="files" className="mt-6">
          <EngFilesTab project={project} canEdit={canEdit && !archived} canManage={canManage} locked={locked} />
        </TabsContent>
        <TabsContent value="prints" className="mt-6">
          <EngPrintsTab project={project} canEdit={canEdit && !archived} />
        </TabsContent>
        <TabsContent value="tasks" className="mt-6">
          <EngTasksTab project={project} canEdit={canEdit && !archived} canManage={canManage && !archived} />
        </TabsContent>
        <TabsContent value="budget" className="mt-6">
          <EngBudgetTab project={project} canEdit={canEdit && !archived} onGoToTab={goToTab} />
        </TabsContent>
      </Tabs>

      <EditEngProjectDialog project={project} open={editOpen} onOpenChange={setEditOpen} canManage={canManage} />
    </div>
  );
}

function LockBanner({ project }: { project: EngProject }) {
  const meta = getEngStatusMeta(project.status);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-info-soft/70 bg-info-soft/20 px-4 py-3 text-sm text-foreground">
      <Lock className="h-4 w-4 shrink-0 text-info" />
      <span>
        <span className="font-medium">Design locked</span> — status is {meta.label.toLowerCase()}; files, folders and
        versions are frozen. Prints, costs and notes can still be logged.
      </span>
      {project.design_locked_at && (
        <span className="text-xs text-muted-foreground">Locked {formatDateTime(project.design_locked_at)}</span>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/engineering"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to engineering projects
    </Link>
  );
}
